import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { useSearchParams, useLocation } from "react-router-dom";
import { Filter, FilterValue } from "@/components/Table/Filters";
import {
  transformSortByToSortParams,
  type SortParams,
  TabMapping,
} from "@/utils/constants/MediaListConst";
import { FetchFilters } from "@/utils/utilFunctions/filters";
import { REQUIRED_FILTERS } from "@/utils/mediautils/mediautils";
import {
  getFilterStorageKey,
  readStoredFilters,
} from "@/utils/utilFunctions/filterStorage";

interface UseMediaListFiltersProps {
  updateIsFilter: (isFilter: boolean) => void;
  setLocalIsLoading: (isLoading: boolean) => void;
  activeType: string;
  setActiveType: (type: string) => void;
  // Filter dependencies
  orgBusTeamConstants: any;
  categoriesConst: any;
  languageConst: any;
  users: any;
  singleCurrentUser: any;
  bunchProductList: any;
  agenciesList: any;
  bunchBusinessList: any;
}

interface UseMediaListFiltersReturn {
  filters: Record<string, FilterValue>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, FilterValue>>>;
  sortParams: SortParams;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hiddenFilter: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setHiddenFilter: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: React.Dispatch<React.SetStateAction<boolean>>;
  availableFilters: Filter[];
  handleSetFilters: (
    filters: Record<string, FilterValue>,
    isSortBy?: boolean
  ) => void;
  handleSortChange: (value: string) => void;
  updateFiltersAndLoadingCallback: (
    updatedFilters: Record<string, FilterValue>,
    isSortBy?: boolean
  ) => void;
  emptyHiddenFilter: () => void;
}

/**
 * Custom hook to manage all filter-related state and logic in MediaList
 * Extracted to improve code organization and reduce component complexity
 */
export const useMediaListFilters = ({
  updateIsFilter,
  setLocalIsLoading,
  setActiveType,
  orgBusTeamConstants,
  categoriesConst,
  languageConst,
  users,
  singleCurrentUser,
  bunchProductList,
  agenciesList,
  bunchBusinessList,
}: UseMediaListFiltersProps): UseMediaListFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Seed filters (including page) from localStorage right on mount, so
  // remounting (e.g. navigating to media detail and back) restores the page
  // the user was on instead of resetting to page 1. The topLevelSection
  // effect below only re-seeds on section *changes*, which doesn't fire on a
  // fresh mount since prevSectionRef already matches the current section.
  const [filters, setFilters] = useState<Record<string, FilterValue>>(() =>
    readStoredFilters(getFilterStorageKey())
  );
  const [sortParams, setSortParams] = useState<SortParams>({ mtuser: "desc" });
  const [showAdvancedFilters, setShowAdvancedFilters] =
    useState<boolean>(false);

  const topLevelSection = location.pathname.split("/")[1] ?? "";
  const prevSectionRef = useRef(topLevelSection);
  useEffect(() => {
    if (prevSectionRef.current === topLevelSection) return;
    prevSectionRef.current = topLevelSection;
    // Coming back from a chopper trim, MediaList's handleClose navigates to
    // /media/{video|shorts}?source=chopper on purpose so the chopper filter
    // re-applies on the destination tab — don't wipe filters/strip the param
    // here, or the appliedChopperSourceRef effect in MediaList.tsx races this
    // reset and loses the filter.
    const isChopperRedirect =
      new URLSearchParams(location.search).get("source") === "chopper";
    if (isChopperRedirect) return;
    setFilters(readStoredFilters(getFilterStorageKey()));
    setSearchParams(
      (prev) => {
        const tab = prev.get("tab");
        return tab ? { tab } : {};
      },
      { replace: true }
    );
  }, [topLevelSection, setSearchParams]);

  // Initialize hiddenFilter based on URL
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [hiddenFilter, setHiddenFilter] = useState<Record<string, any>>(() => {
    const pathname = window.location.pathname;
    const assetTypeFromPath = pathname.split("/").pop();
    const tabParam = searchParams.get("tab");
    const resolvedTab = tabParam || assetTypeFromPath;

    if (pathname.startsWith("/drive")) {
      const driveAssetType = pathname.split("/").pop();
      return {
        asset_type: driveAssetType === "shorts" ? "shorts" : "video",
        content_type: 2,
      };
    }
    if (resolvedTab === "premiere") {
      return { asset_type: "video", ctype: ["premiere"] };
    }
    if (resolvedTab === "video") {
      return {
        asset_type: ["video", "campaign", "slates"],
        must_not: { ctype: ["premiere"], content_type: 2 },
      };
    }
    if (resolvedTab && Object.keys(TabMapping).includes(resolvedTab)) {
      return {
        asset_type: resolvedTab,
        must_not: { ctype: ["premiere"], content_type: 2 },
      };
    }
    return {
      asset_type: ["video", "campaign", "slates"],
      must_not: { ctype: ["premiere"], content_type: 2 },
    };
  });

  const prevPathForPageRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathForPageRef.current === location.pathname) return;
    prevPathForPageRef.current = location.pathname;
    setFilters((prev) => {
      if (prev?.page == null || Number(prev.page) <= 1) return prev;
      const next = { ...prev };
      delete next.page;
      return next;
    });
  }, [location.pathname]);

  // Update hiddenFilter when activeType or location changes
  useEffect(() => {
    const pathname = location.pathname;

    if (pathname.startsWith("/drive")) {
      const driveAssetType = pathname.split("/").pop();
      setActiveType(DRIVE_TAB);
      setHiddenFilter({
        asset_type: driveAssetType === "shorts" ? "shorts" : "video",
        content_type: 2,
      });
      return;
    }

    const assetTypeFromPath = pathname.split("/").pop();
    if (
      assetTypeFromPath &&
      Object.keys(TabMapping).includes(assetTypeFromPath)
    ) {
      setActiveType(assetTypeFromPath);
      if (assetTypeFromPath === "premiere") {
        setHiddenFilter({ asset_type: "video", ctype: ["premiere"] });
      } else if (assetTypeFromPath === "video") {
        setHiddenFilter({
          asset_type: ["video", "campaign", "slates"],
          must_not: { ctype: ["premiere"], content_type: 2 },
        } as Record<string, FilterValue>);
      } else {
        setHiddenFilter({
          asset_type: assetTypeFromPath,
          must_not: { ctype: ["premiere"], content_type: 2 },
        } as Record<string, FilterValue>);
      }
    }
  }, [location.pathname, setActiveType]);

  // Update isFilter state based on filters
  useEffect(() => {
    // Filter out keys with undefined values before counting
    const validFilters = Object.entries(filters).filter(
      ([_, value]) => value !== undefined
    );

    if (validFilters.length > 0) {
      updateIsFilter(true);
    } else {
      updateIsFilter(false);
    }
  }, [filters, updateIsFilter]);

  // Memoize expensive FetchFilters computation
  const availableFilters = useMemo(() => {
    return FetchFilters(
      orgBusTeamConstants,
      categoriesConst,
      languageConst,
      users,
      singleCurrentUser,
      bunchProductList,
      agenciesList,
      bunchBusinessList,
      "mediaList",
      REQUIRED_FILTERS
    );
  }, [
    categoriesConst,
    languageConst,
    orgBusTeamConstants,
    users,
    singleCurrentUser,
    bunchBusinessList,
    bunchProductList,
    agenciesList,
  ]);

  /**
   * Updates filters and triggers loading state
   * Handles sort parameter transformation and advanced filters visibility
   */
  const updateFiltersAndLoadingCallback = useCallback(
    (updatedFilters: Record<string, FilterValue>, isSortBy?: boolean) => {
      const isSortOnlyOperation =
        updatedFilters.sortBy &&
        Object.keys(updatedFilters).every(
          (key) => key === "sortBy" || key === "sort"
        );
      if (updatedFilters.sortBy && typeof updatedFilters.sortBy === "string") {
        const sortParams = transformSortByToSortParams(updatedFilters.sortBy);
        setSortParams(sortParams);
        const filtersWithSort = { ...updatedFilters, sort: sortParams };
        if (isSortBy || isSortOnlyOperation) {
          delete (filtersWithSort as Record<string, FilterValue>).page;
          setShowAdvancedFilters(false);
        }
        setFilters(filtersWithSort as Record<string, FilterValue>);
      } else {
        setSortParams({ mtuser: "desc" });
        setFilters(updatedFilters);
      }
      if (
        Object.keys(updatedFilters).length > 1 &&
        !isSortBy &&
        !isSortOnlyOperation
      ) {
        setShowAdvancedFilters(true);
      }
      setLocalIsLoading(true);
    },
    [setLocalIsLoading]
  );

  /**
   * Handler for setting filters
   */
  const handleSetFilters = useCallback(
    (filters: Record<string, FilterValue>, isSortBy?: boolean) => {
      updateFiltersAndLoadingCallback(filters, isSortBy || false);
    },
    [updateFiltersAndLoadingCallback]
  );

  /**
   * Handler for sort change
   */
  const handleSortChange = useCallback(
    (value: string) => {
      handleSetFilters({ ...filters, sortBy: value }, true);
    },
    [filters, handleSetFilters]
  );

  /**
   * Clears hidden filter and resets active type to video
   */
  const emptyHiddenFilter = useCallback(() => {
    setHiddenFilter({});
    setActiveType("video");
  }, [setActiveType]);

  // When the Uploader (ctby) filter is active, strip `team` from the hidden
  // filter so the backend searches across all teams for that uploader.
  const effectiveHiddenFilter = useMemo(() => {
    let result = hiddenFilter;

    if (filters.ctby != null) {
      const { team: _team, ...rest } = result as any;
      result = rest;
    }

    if (String(filters.media_status) === "3") {
      result = { ...result, include_archived: true, archive: 1 };
    }

    return result;
  }, [hiddenFilter, filters.ctby, filters.media_status]);

  return {
    filters,
    setFilters,
    sortParams,
    hiddenFilter: effectiveHiddenFilter,
    setHiddenFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    availableFilters,
    handleSetFilters,
    handleSortChange,
    updateFiltersAndLoadingCallback,
    emptyHiddenFilter,
  };
};
